import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import { Card } from '../../../../components/ui/Card';
import type { CampaignCreationState, GmailConnection } from '../types';

interface DirectSendStepProps {
  campaignState: CampaignCreationState;
  gmailConnections: GmailConnection[];
  columns: { key: string; name: string }[];
  emailColumn: string;
  onEmailColumnChange: (column: string) => void;
  error: string | null;
  isLoading: boolean;
  onUpdateState: (updates: Partial<CampaignCreationState>) => void;
  onSend: () => void;
  onEnrichEmail?: () => void;
}

interface SendingStepProps {
  leadCount: number;
  sendProgress: number;
  sendResults: { success: number; failed: number };
}

interface CompleteStepProps {
  sendResults: { success: number; failed: number; lastError?: string };
  campaignId?: string | null;
  onDone: () => void;
}

export const DirectSendStep: React.FC<DirectSendStepProps> = ({
  campaignState,
  gmailConnections,
  columns,
  emailColumn,
  onEmailColumnChange,
  error,
  isLoading,
  onUpdateState,
  onSend,
  onEnrichEmail,
}) => {
  const activeConnections = gmailConnections.filter(c => c.token_status === 'active');

  // Filter columns that might contain email addresses
  const emailColumns = columns.filter(col =>
    col.key.toLowerCase().includes('email') ||
    col.name.toLowerCase().includes('email')
  );
  // If no email columns found, show all columns
  const columnsToShow = emailColumns.length > 0 ? emailColumns : columns;

  const sendButtonText = isLoading
    ? 'Preparing...'
    : `Send ${campaignState.leadCount.toLocaleString()} ${campaignState.leadCount === 1 ? 'email' : 'emails'} now`;

  return (
    <div className="mt-3">
      {/* Settings card */}
      <Card padding="none" shadow="none" className="overflow-hidden">
        {/* Email Account */}
        <div className="p-3 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-neutral-600">Send from</span>
            {activeConnections.length === 0 ? (
              <span className="text-[12px] text-amber-600">Connect Gmail in Settings</span>
            ) : (
              <select
                value={campaignState.emailAccountId || ''}
                onChange={(e) => {
                  const conn = gmailConnections.find(c => c.id === e.target.value);
                  onUpdateState({
                    emailAccountId: conn?.id,
                    emailAccount: conn,
                  });
                }}
                className="px-2 py-1.5 text-[12px] bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-300"
              >
                {activeConnections.map((conn) => (
                  <option key={conn.id} value={conn.id}>
                    {conn.gmail_email}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Email Column Selection */}
        <div className="p-3 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-neutral-600">Send to column</span>
            <select
              value={emailColumn}
              onChange={(e) => onEmailColumnChange(e.target.value)}
              className="px-2 py-1.5 text-[12px] bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-300"
            >
              <option value="">Select column</option>
              {columnsToShow.map((col) => (
                <option key={col.key} value={col.key}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
          {onEnrichEmail && (
            <div className="mt-2 flex items-center justify-end">
              <span className="text-[11px] text-neutral-500">No email column?</span>
              <Button
                variant="link"
                size="xs"
                onClick={onEnrichEmail}
                className="ml-1 text-[11px] underline"
              >
                Enrich emails
              </Button>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="p-3">
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <div>
              <span className="text-neutral-500">Template:</span>
              <p className="font-medium text-neutral-900">{campaignState.template?.name}</p>
            </div>
            <div>
              <span className="text-neutral-500">Recipients:</span>
              <p className="font-medium text-neutral-900">{campaignState.leadCount.toLocaleString()} leads</p>
            </div>
          </div>
        </div>
      </Card>

      {error && (
        <div className="mt-3 flex items-center gap-1.5 p-2 bg-red-50 border border-red-100 rounded-lg">
          <AlertCircle className="w-3 h-3 text-red-500" />
          <span className="text-[11px] text-red-600">{error}</span>
        </div>
      )}

      {/* Warning if more than 20 leads selected */}
      {campaignState.leadCount > 20 && (
        <div className="mt-3 flex items-center gap-1.5 p-2 bg-amber-50 border border-amber-100 rounded-lg">
          <AlertCircle className="w-3 h-3 text-amber-500" />
          <span className="text-[11px] text-amber-700">
            Only first 20 emails will be sent. Select fewer leads or use campaign scheduling for larger sends.
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4">
        <Button
          variant="primary"
          size="sm"
          loading={isLoading}
          disabled={!campaignState.emailAccountId || !emailColumn || activeConnections.length === 0}
          onClick={onSend}
        >
          {sendButtonText}
        </Button>
      </div>
    </div>
  );
};

export const SendingStep: React.FC<SendingStepProps> = ({
  leadCount,
  sendProgress,
  sendResults,
}) => {
  return (
    <Card padding="lg" shadow="none" className="mt-3 text-center">
      <div className="flex justify-center mb-4">
        <Spinner size="lg" />
      </div>
      <h3 className="text-[14px] font-semibold text-neutral-900 mb-2">Sending Emails...</h3>
      <p className="text-[12px] text-neutral-500 mb-4">
        {sendResults.success + sendResults.failed} of {leadCount} processed
      </p>
      <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-neutral-900 rounded-full transition-all duration-300"
          style={{ width: `${sendProgress}%` }}
        />
      </div>
      <p className="text-[11px] text-neutral-400 mt-2">{sendProgress}% complete</p>
    </Card>
  );
};

export const CompleteStep: React.FC<CompleteStepProps> = ({
  sendResults,
  campaignId,
  onDone,
}) => {
  const navigate = useNavigate();
  const hasFailures = sendResults.failed > 0;

  const handleViewCampaign = () => {
    if (campaignId) {
      navigate(`/campaigns/${campaignId}`);
    }
  };

  return (
    <Card padding="lg" shadow="none" className="mt-3 text-center">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
        hasFailures ? 'bg-amber-50' : 'bg-green-50'
      }`}>
        {hasFailures ? (
          <AlertCircle className="w-6 h-6 text-amber-600" />
        ) : (
          <CheckCircle className="w-6 h-6 text-green-600" />
        )}
      </div>
      <h3 className="text-[14px] font-semibold text-neutral-900 mb-2">
        {hasFailures ? 'Sending Complete' : 'Emails Sent!'}
      </h3>
      <p className={`text-[12px] text-neutral-500 ${hasFailures && sendResults.lastError ? 'mb-2' : 'mb-4'}`}>
        {sendResults.success} sent successfully
        {hasFailures && `, ${sendResults.failed} failed`}
      </p>
      {hasFailures && sendResults.lastError && (
        <p className="text-[11px] text-red-600 mb-4 px-4">
          Error: {sendResults.lastError}
        </p>
      )}
      <div className="flex items-center justify-center gap-2">
        {campaignId && (
          <Button variant="secondary" size="sm" onClick={handleViewCampaign}>
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
            View Campaign
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </Card>
  );
};
